import React from "react";
import CodeBlock from "../components/CodeBlock";

const IntermediateTutorial = () => (
  <section id="intermediate" className="developer-section">
    <h2>Intermediate Tutorial</h2>
    <p>
      Now that you can run basic searches, let's explore similarity tuning, filtering,
      entity resolution workflows, and data export.
    </p>

    <h3>Similarity Search Parameters</h3>
    <p>
      Control how aggressively the graph is traversed with these parameters on <code>/api/search</code>.
      <strong>These are query-string parameters, not body fields</strong> &mdash; the body only takes
      <code>ids</code> (and, for chat, <code>messages</code>). Passing them in the body is silently
      ignored.
    </p>
    <table className="dev-api-params">
      <thead>
        <tr><th>Parameter</th><th>Type</th><th>Default</th><th>Description</th></tr>
      </thead>
      <tbody>
        <tr>
          <td><code>sim_threshold</code></td>
          <td>float</td>
          <td>0.75</td>
          <td>Minimum similarity score (0.0&ndash;1.0). Lower values return more results with weaker connections.</td>
        </tr>
        <tr>
          <td><code>sim_type</code></td>
          <td>string</td>
          <td>"sim"</td>
          <td>Similarity algorithm. Options depend on server config (e.g., "sim", "org").</td>
        </tr>
        <tr>
          <td><code>sim_method</code></td>
          <td>string</td>
          <td>"tanimoto"</td>
          <td>Fingerprint comparison method for chemical similarity.</td>
        </tr>
        <tr>
          <td><code>sim_substructure</code></td>
          <td>boolean</td>
          <td>false</td>
          <td>Use substructure matching instead of full-molecule similarity.</td>
        </tr>
        <tr>
          <td><code>find_related</code></td>
          <td>boolean</td>
          <td>false</td>
          <td>Enable pseudo-similarity search for broader results.</td>
        </tr>
      </tbody>
    </table>

    <CodeBlock
      title="Similarity search with custom threshold"
      examples={{
        curl: `curl -s "/api/search?sim_threshold=0.6&sim_method=tanimoto&find_related=true&page_size=25" \\
  -H "x-api-key: $PLEX_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"ids": ["unichem:161671"]}' | jq '.totals'`,
        python: `resp = requests.post(
    "/api/search",
    headers={"x-api-key": token, "Content-Type": "application/json"},
    params={
        "sim_threshold": 0.6,
        "sim_method": "tanimoto",
        "find_related": True,
        "page_size": 25,
    },
    json={"ids": ["unichem:161671"]}
)
data = resp.json()
print("Result counts by category:")
for cat, count in data["totals"].items():
    print(f"  {cat}: {count}")`,
        javascript: `const params = new URLSearchParams({
  sim_threshold: 0.6,
  sim_method: "tanimoto",
  find_related: true,
  page_size: 25,
});
const resp = await fetch(\`/api/search?\${params}\`, {
  method: "POST",
  headers: {
    "x-api-key": token,
    "Content-Type": "application/json"
  },
  body: JSON.stringify({ ids: ["unichem:161671"] })
});
const data = await resp.json();
console.log("Result counts:", data.totals);`,
      }}
    />

    <h3>Results by search_id</h3>
    <p>
      A search answers with its <code>search_id</code>. The dataset filter, exports, evidence
      chains and the network each have their own endpoint (<code>/api/connected</code>,
      <code>/api/export</code>, <code>/api/evidence</code>, <code>/api/network</code>), which
      takes that <code>search_id</code> once the search has finished. They never start a search:
      a <code>202</code> means it is still running (ask again), and a <code>404</code> means its
      results have expired (run the search again). <code>/api/search</code> refuses their
      parameters.
    </p>

    <h3>Dataset Filtering</h3>
    <p>
      Find which results in a category have evidence from specific data sources: run the search,
      then ask <code>/api/connected</code> with its <code>search_id</code>, a
      <code>category</code> and <code>datasets_filter</code>:
    </p>
    <CodeBlock
      title="Filter by datasets"
      examples={{
        curl: `# First, list available datasets
curl -s /api/datasets \\
  -H "x-api-key: $PLEX_TOKEN" | jq '.[].id'

# Run the search; re-run this until .status is 200
SEARCH_ID=$(curl -s /api/search \\
  -H "x-api-key: $PLEX_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"ids": ["unichem:161671"]}' | jq -r '.search_id')

# Then ask which diseases have evidence from these datasets
curl -s /api/connected \\
  -H "x-api-key: $PLEX_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d "{\\"search_id\\": \\"$SEARCH_ID\\", \\"category\\": \\"disease\\", \\"datasets_filter\\": [\\"chembl\\", \\"pubchem\\"]}" | jq .ids`,
        python: `import time

HEADERS = {"x-api-key": token, "Content-Type": "application/json"}

# List available datasets
datasets = requests.get("/api/datasets", headers={"x-api-key": token}).json()
print("Available:", [d["id"] for d in datasets])

# Run the search and wait for it to finish
search = requests.post("/api/search", headers=HEADERS, json={"ids": ["unichem:161671"]}).json()
while search["status"] != 200:
    time.sleep(2)
    search = requests.post("/api/search", headers=HEADERS, json={"ids": ["unichem:161671"]}).json()

# Which diseases have evidence from these datasets?
connected = requests.post(
    "/api/connected",
    headers=HEADERS,
    json={
        "search_id": search["search_id"],
        "category": "disease",
        "datasets_filter": ["chembl", "pubchem"],
    }
).json()
print(connected["ids"])`,
        javascript: `const headers = { "x-api-key": token, "Content-Type": "application/json" };

// List available datasets
const datasets = await fetch("/api/datasets", {
  headers: { "x-api-key": token }
}).then(r => r.json());
console.log("Available:", datasets.map(d => d.id));

// Run the search and wait for it to finish
const runSearch = () => fetch("/api/search", {
  method: "POST", headers,
  body: JSON.stringify({ ids: ["unichem:161671"] })
}).then(r => r.json());
let search = await runSearch();
while (search.status !== 200) {
  await new Promise(resolve => setTimeout(resolve, 2000));
  search = await runSearch();
}

// Which diseases have evidence from these datasets?
const connected = await fetch("/api/connected", {
  method: "POST", headers,
  body: JSON.stringify({
    search_id: search.search_id,
    category: "disease",
    datasets_filter: ["chembl", "pubchem"]
  })
}).then(r => r.json());
console.log(connected.ids);`,
      }}
    />

    <h3>Entity Resolution Workflow</h3>
    <p>
      A common pattern is to resolve user input into entity IDs, then run a search.
      Here is the full workflow:
    </p>
    <CodeBlock
      title="Full entity resolution workflow"
      examples={{
        python: `import requests

BASE = ""
HEADERS = {"x-api-key": token, "Content-Type": "application/json"}

# 1. Resolve free-text to entity IDs
suggestions = requests.post(
    f"{BASE}/api/suggestions",
    headers=HEADERS,
    json={"terms": ["aspirin", "EGFR"]}
).json()

# Pick the best match for each term, preferring the most confident match-type
RANK = ["resolved-definitive", "resolved", "keyword", "completion", "fts"]
entity_ids = [s["id"] for s in suggestions if s.get("match-type") in RANK]
print(f"Resolved IDs: {entity_ids}")

# 2. Get full entity details
entities = requests.post(
    f"{BASE}/api/entities",
    headers=HEADERS,
    params={"partial": "true"},
    json=entity_ids
).json()["entities"]

for e in entities:
    if e:
        print(f"  {e['id']}: {e['title']} ({e['category']})")

# 3. Search with resolved IDs
results = requests.post(
    f"{BASE}/api/search",
    headers=HEADERS,
    params={"page_size": 20},
    json={"ids": entity_ids}
).json()

print(f"Found results in categories: {list(results['totals'].keys())}")`,
        javascript: `const BASE = "";
const headers = {
  "x-api-key": token,
  "Content-Type": "application/json"
};

// 1. Resolve free-text to entity IDs
const suggestions = await fetch(\`\${BASE}/api/suggestions\`, {
  method: "POST", headers,
  body: JSON.stringify({ terms: ["aspirin", "EGFR"] })
}).then(r => r.json());

// Prefer the most confident match-type (keyword matches have no match-score)
const RANK = ["resolved-definitive", "resolved", "keyword", "completion", "fts"];
const entityIds = suggestions
  .filter(s => RANK.includes(s["match-type"]))
  .map(s => s.id);
console.log("Resolved IDs:", entityIds);

// 2. Get full entity details
const { entities } = await fetch(\`\${BASE}/api/entities?partial=true\`, {
  method: "POST", headers,
  body: JSON.stringify(entityIds)
}).then(r => r.json());

entities.filter(Boolean).forEach(e =>
  console.log(\`  \${e.id}: \${e.title} (\${e.category})\`)
);

// 3. Search with resolved IDs
const results = await fetch(\`\${BASE}/api/search?page_size=20\`, {
  method: "POST", headers,
  body: JSON.stringify({ ids: entityIds })
}).then(r => r.json());

console.log("Categories:", Object.keys(results.totals));`,
        curl: `# 1. Resolve names
curl -s /api/suggestions \\
  -H "x-api-key: $PLEX_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"terms": ["aspirin", "EGFR"]}' | jq '.[].id'

# 2. Get entity details (note: response is {"entities": [...]}, not a bare array)
curl -s "/api/entities?partial=true" \\
  -H "x-api-key: $PLEX_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '["unichem:161671", "entrezgene:1956"]' | jq .entities

# 3. Search
curl -s "/api/search?page_size=20" \\
  -H "x-api-key: $PLEX_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"ids": ["unichem:161671", "entrezgene:1956"]}' | jq .totals`,
      }}
    />

    <h3>Exporting Results</h3>
    <p>
      Export a finished search's results in CSV or XLSX format: POST its <code>search_id</code>
      and an <code>export_format</code> to <code>/api/export</code> (see Results by search_id
      above for running the search first):
    </p>
    <CodeBlock
      title="Export to CSV"
      examples={{
        curl: `curl -s /api/export \\
  -H "x-api-key: $PLEX_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d "{\\"search_id\\": \\"$SEARCH_ID\\", \\"export_format\\": \\"csv\\"}" \\
  -o results.csv`,
        python: `resp = requests.post(
    "/api/export",
    headers={"x-api-key": token, "Content-Type": "application/json"},
    json={"search_id": search["search_id"], "export_format": "csv"}
)
with open("results.csv", "wb") as f:
    f.write(resp.content)
print("Exported to results.csv")`,
        javascript: `const resp = await fetch("/api/export", {
  method: "POST",
  headers: {
    "x-api-key": token,
    "Content-Type": "application/json"
  },
  body: JSON.stringify({ search_id: search.search_id, export_format: "csv" })
});
const blob = await resp.blob();
// In Node.js: fs.writeFileSync("results.csv", Buffer.from(await resp.arrayBuffer()));`,
      }}
    />
  </section>
);

export default IntermediateTutorial;
